import { useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { C, ink } from '../lib/theme';
import { useTheme } from '../lib/useTheme';
import type { Me } from '../lib/types';
import { Avatar } from './Avatar';
import { AvatarCropper } from './AvatarCropper';
import { IconMoon, IconSun } from './icons';
import { ScaleModal, type Origin } from './ScaleModal';
import { useToast } from './Toast';

/**
 * Configurações pessoais (nível 2, Scale Modal) — hoje só a foto de perfil,
 * mas é o lugar certo pra qualquer preferência pessoal futura (não é gestão
 * de usuários, que é admin-only e mora em `/admin/usuarios`). Aberto pelo
 * item "Configurações" do menu de perfil (header) — o botão duplicado que
 * existia no rodapé da sidebar foi removido, essa é a única entrada agora.
 */
export function SettingsModal({ me, origin, onClose, onMeUpdated }: {
  me: Me;
  origin: Origin | null;
  onClose: () => void;
  onMeUpdated: (patch: Partial<Me>) => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Não sobe direto — primeiro passa pelo editor de posição/zoom, pra o
    // usuário escolher o enquadramento antes de gravar (pedido explícito).
    setCropFile(file);
  }

  async function confirmCrop(blob: Blob) {
    setBusy(true);
    try {
      const cropped = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      const res = await api.uploadAvatar(cropped);
      onMeUpdated({ avatar_url: res.avatar_url });
      toast('Foto atualizada.', 'ok');
      setCropFile(null);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao enviar a foto.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function removePhoto() {
    setBusy(true);
    try {
      await api.removeAvatar();
      onMeUpdated({ avatar_url: null });
      toast('Foto removida.', 'ok');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao remover a foto.', 'error');
    } finally {
      setBusy(false);
    }
  }

  const isAdmin = me.role === 'admin';
  const { mode, toggle } = useTheme();
  const isLight = mode === 'light';

  return (
    <ScaleModal
      open={origin !== null} origin={origin} onClose={() => { setCropFile(null); onClose(); }}
      title="Configurações pessoais" width={420}
    >
      {cropFile ? (
        <AvatarCropper file={cropFile} busy={busy} onCancel={() => setCropFile(null)} onConfirm={confirmCrop} />
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '6px 0 4px' }}>
        <Avatar url={me.avatar_url} email={me.email} size={104} ring />

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: ink(0.9) }}>{me.email}</div>
          <span style={{
            display: 'inline-block', marginTop: 7, fontSize: 10, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
            background: isAdmin ? 'rgba(255,144,112,0.16)' : ink(0.09),
            color: isAdmin ? C.accent : ink(0.6),
          }}>{isAdmin ? 'administrador' : 'usuário'}</span>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            type="button" disabled={busy} onClick={() => inputRef.current?.click()}
            style={{
              border: 'none', cursor: busy ? 'wait' : 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 700,
              padding: '9px 16px', borderRadius: 11, color: '#fff', opacity: busy ? 0.6 : 1,
              background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`,
              boxShadow: '0 6px 18px -2px rgba(232,85,48,0.5)',
            }}
          >{me.avatar_url ? 'Trocar foto' : 'Adicionar foto'}</button>

          {me.avatar_url && (
            <button
              type="button" disabled={busy} onClick={removePhoto}
              style={{
                border: `1px solid ${ink(0.15)}`, background: ink(0.05), color: ink(0.7),
                cursor: busy ? 'wait' : 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 650,
                padding: '9px 14px', borderRadius: 11, opacity: busy ? 0.6 : 1,
              }}
            >Remover</button>
          )}
        </div>

        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={pickFile} style={{ display: 'none' }} />
        <div style={{ fontSize: 11.5, color: ink(0.35), textAlign: 'center' }}>PNG, JPEG ou WEBP · até 4MB</div>

        {/* Toggle de tema (Etapa 2 do plano de UX/UI, 2026-09-10) — manual,
            não segue o sistema (decisão confirmada). Escuro continua sendo
            o padrão; o claro ainda está em calibração (Etapa 3), então
            trocar aqui já funciona de ponta a ponta mas pode mostrar cor
            não-refinada em telas específicas por enquanto. */}
        <div style={{
          width: '100%', marginTop: 6, paddingTop: 16, borderTop: `1px solid ${ink(0.08)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            <span style={{
              width: 30, height: 30, borderRadius: 10, flex: 'none', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,144,112,0.14)', color: C.accent,
            }}>{isLight ? <IconSun size={15} /> : <IconMoon size={15} />}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: ink(0.85) }}>Tema</div>
              <div style={{ fontSize: 11, color: ink(0.42) }}>{isLight ? 'Claro' : 'Escuro'}</div>
            </div>
          </div>
          <button
            type="button" role="switch" aria-checked={isLight} onClick={toggle}
            style={{
              width: 42, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer', padding: 0,
              flex: 'none', position: 'relative', background: isLight ? C.accent : ink(0.16),
              transition: 'background 0.2s ease',
            }}
          >
            <span style={{
              position: 'absolute', top: 3, left: isLight ? 21 : 3, width: 18, height: 18, borderRadius: '50%',
              background: '#fff', transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </button>
        </div>
      </div>
      )}
    </ScaleModal>
  );
}
